'use client'

import { WIDE_ASPECT_RATIO } from '@/components/scenario/aspectRatio'
import { Volume2 } from 'lucide-react'
import { FC, useEffect, useRef, useState } from 'react'
import type { Hotspot, InteractiveButton, PlayerConfig } from '../../../types/interactiveVideo'
import { useShadowHostPortal } from '../shadow-host-portal/ShadowHostPortalContext'
import UnifiedPlayMark from '../UnifiedPlayMark'
import WideModal from './WideModal'
import { WIDE_FONT_FAMILY } from './wideFont'

export type WidePlayerProps = {
  scenarioId: string
  interactiveConfig: PlayerConfig
  /** 再生前に見せるポスター（初期ノードのサムネイル or シナリオの入口サムネイル） */
  posterUrl: string | null
  posterMimeType: string | null
  posterTitle?: string | null
  posterDuration?: number | null
  onStart: () => void
  /** モーダルを閉じた（視聴終了の計測に使う） */
  onClose: () => void
  onVideoEnd: (nodeId: string) => void
  onVideoChange: (nodeId: string) => void
  onButtonClick: (button: InteractiveButton) => void
  /** 動画内のクリック領域が押された（計測用・ADR-0022） */
  onHotspotClick?: (hotspot: Hotspot) => void | Promise<void>
}

/**
 * ワイド型のシェル。ページ内に横型16:9のポスターを置き、クリックでモーダルを開いて大きく再生する。
 *
 * ポスターの段階では `InteractiveVideoPlayer`（＝video要素）をマウントしない。ページに常駐する
 * 性質上、開いただけで動画バイトを取りに行くと埋め込み先の表示速度を損なうため
 * （スクリプト自体の初期化は embedded.js の IntersectionObserver が画面到達まで遅らせている）。
 *
 * ポスター枠は常に 16:9 でページのフローに残り、再生は上に重なるモーダルで行うので、
 * 埋め込み先ページのレイアウトは開閉で一切動かない（ADR-0016）。
 *
 * モーダルを開いている間は shadowHost を body 直下へ退避させる（BOX/SLIDER/TILE と同じ）。
 * インライン配置のままだと、埋め込み先の `overflow: hidden` や `transform` を持つ祖先に
 * 閉じ込められて全画面オーバーレイが成立しないため。
 */
const WidePlayer: FC<WidePlayerProps> = ({
  scenarioId,
  interactiveConfig,
  posterUrl,
  posterMimeType,
  posterTitle,
  posterDuration,
  onStart,
  onClose,
  onVideoEnd,
  onVideoChange,
  onButtonClick,
  onHotspotClick,
}) => {
  const portal = useShadowHostPortal()
  const [isOpen, setIsOpen] = useState(false)
  const [isCompact, setIsCompact] = useState(false)
  const [previewDuration, setPreviewDuration] = useState(posterDuration ?? 0)
  const frameRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    portal?.setPortalActive(isOpen)
  }, [isOpen, portal])

  useEffect(() => {
    const element = frameRef.current
    if (!element) return

    const sync = () => setIsCompact(element.getBoundingClientRect().width < 520)
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const handleStart = () => {
    setIsOpen(true)
    onStart()
  }

  const handleClose = () => {
    setIsOpen(false)
    onClose()
  }

  const isPosterVideo = posterMimeType?.startsWith('video/')
  const formatDuration = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds <= 0) return null
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.floor(seconds % 60)
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }
  const durationLabel = formatDuration(previewDuration)

  return (
    <>
      {/* ポスターはモーダルを開いている間もアンマウントしない（閉じたときにページの高さが跳ねるため）。
          開いている間は埋め込み全体が画面に固定した層へ移り、ポスターが画面上端に出てモーダルの後ろで
          ぼけて見えてしまうので、見えなくする。ページ側には同じ高さの空き枠が残る（ShadowHostPortal） */}
      <div
        ref={frameRef}
        data-testid="wide-player"
        style={{
          visibility: isOpen ? 'hidden' : 'visible',
          position: 'relative',
          width: '100%',
          aspectRatio: WIDE_ASPECT_RATIO,
          overflow: 'hidden',
          background: '#0f172a',
          border: '1px solid rgba(148, 163, 184, 0.28)',
          borderRadius: '14px',
          boxShadow: 'none',
          fontFamily: WIDE_FONT_FAMILY,
        }}
      >
        <div
          className="group"
          role="button"
          tabIndex={0}
          aria-label="動画を再生"
          onClick={handleStart}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleStart()
            }
          }}
          style={{ position: 'absolute', inset: 0, cursor: 'pointer' }}
        >
          {posterUrl &&
            (isPosterVideo ? (
              <video
                src={posterUrl}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                autoPlay
                muted
                loop
                playsInline
                onLoadedMetadata={(event) => {
                  if (!posterDuration) setPreviewDuration(event.currentTarget.duration)
                }}
              />
            ) : (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={posterUrl}
                alt=""
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ))}

          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(15,23,42,0.04) 34%, rgba(15,23,42,0.16) 58%, rgba(8,18,38,0.88) 100%)',
            }}
          />

          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <UnifiedPlayMark />
          </div>

          <div
            style={{
              position: 'absolute',
              right: isCompact ? '14px' : '24px',
              bottom: isCompact ? '13px' : '21px',
              left: isCompact ? '14px' : '24px',
              display: 'flex',
              alignItems: 'flex-end',
              gap: '16px',
              color: '#fff',
              pointerEvents: 'none',
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  overflow: 'hidden',
                  margin: 0,
                  color: '#fff',
                  fontSize: isCompact ? '14px' : '18px',
                  fontWeight: 780,
                  lineHeight: 1.45,
                  letterSpacing: '-0.015em',
                  textOverflow: 'ellipsis',
                  textShadow: '0 2px 12px rgba(2,6,23,0.42)',
                  whiteSpace: 'nowrap',
                }}
              >
                {posterTitle || 'インタラクティブ動画'}
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isCompact ? '8px' : '11px',
                  marginTop: isCompact ? '5px' : '7px',
                  color: 'rgba(255,255,255,0.82)',
                  fontSize: isCompact ? '9px' : '10px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                }}
              >
                {durationLabel && <span style={{ fontVariantNumeric: 'tabular-nums' }}>{durationLabel}</span>}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <Volume2 size={isCompact ? 10 : 11} />
                  音声あり
                </span>
              </div>
            </div>

            {!isCompact && (
              <span
                style={{
                  padding: '6px 9px',
                  border: '1px solid rgba(255,255,255,0.24)',
                  borderRadius: '999px',
                  background: 'rgba(15,23,42,0.28)',
                  color: 'rgba(255,255,255,0.88)',
                  fontSize: '9px',
                  fontWeight: 750,
                  letterSpacing: '0.08em',
                  backdropFilter: 'blur(6px)',
                }}
              >
                CLICK TO PLAY
              </span>
            )}
          </div>
        </div>
      </div>

      {isOpen && (
        <WideModal
          scenarioId={scenarioId}
          config={interactiveConfig}
          onClose={handleClose}
          onVideoEnd={onVideoEnd}
          onVideoChange={onVideoChange}
          onButtonClick={onButtonClick}
          onHotspotClick={onHotspotClick}
        />
      )}
    </>
  )
}

export default WidePlayer
